import { useCallback, useEffect, useRef, useState } from "react";
import { useAlertContext, useUserContext } from "../contexts";
import { BuildUrl } from "../helpers";
import { AlertType, Clinic, BannedPatientMessage } from "../models";
import { useLocation, useNavigate } from "react-router";
import { apiAccessRequest, ROUTES } from "../constants";
import { useMsal } from "@azure/msal-react";
import CircularProgress from "@mui/material/CircularProgress";

export enum FromPage {
	BannedPatient = "BannedPatient"
}
export const BannedPatientMessageEdit = () => {
	const [clinic, setClinic] = useState<Clinic | null>(null);
	const { state } = useLocation();
	const navigate = useNavigate();
	const { showAlert } = useAlertContext();
	const { account } = useUserContext();
	const { instance } = useMsal();
	const [bannedPatientMessage, setBannedPatientMessage] =
		useState<BannedPatientMessage | null>(null);
	const messageEl = useRef<HTMLTextAreaElement | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const getBannedPatientMessage = useCallback(async () => {
		try {
			setIsLoading(true);
			const requestUrl = BuildUrl(`Clinic/BannedPatientMessage`);
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				...(tokenResponse && {
					Authorization: `Bearer ${tokenResponse.accessToken}`
				}),
				...{ "Content-Type": "application/json" }
			};
			const response = await fetch(requestUrl, {
				method: "GET",
				headers
			});

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}

			const contentLength = response.headers.get("Content-Length");
			if (response.status !== 204 && contentLength !== "0") {
				const result = await response.json();
				console.info("getBannedPatientMessage", result);
				setBannedPatientMessage(result);
			}
		} catch (error) {
			console.info(error);
			showAlert({
				message: "Banned Patient Message could not be retrieved",
				alertType: AlertType.error
			});
			return;
		} finally {
			setIsLoading(false);
		}
	}, [account, instance, showAlert]);

	useEffect(() => {
		if (state?.clinic) {
			setClinic(state?.clinic);
			getBannedPatientMessage();
		}
	}, [clinic, getBannedPatientMessage, state?.clinic]);

	const updateBannedPatientMessage = useCallback(async () => {
		const message = messageEl.current?.value;

		try {
			setIsLoading(true);
			const requestUrl = BuildUrl(`Clinic/BannedPatientMessage`);
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				...(tokenResponse && {
					Authorization: `Bearer ${tokenResponse.accessToken}`
				}),
				...{ "Content-Type": "application/json" }
			};

			console.info("updateBannedPatientMessage", message);
			const body = {
				...bannedPatientMessage,
				message
			};
			const response = await fetch(requestUrl, {
				method: bannedPatientMessage != null ? "PATCH" : "POST",
				headers,
				body: JSON.stringify(body)
			});

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}

			const contentLength = response.headers.get("Content-Length");
			if (response.status !== 204 && contentLength !== "0") {
				const result = await response.json();
				console.info("updateBannedPatientMessage", result);
				setBannedPatientMessage(result);
				showAlert({
					message: "Banned Patient Message was updated",
					alertType: AlertType.success
				});
			}
		} catch (error) {
			console.info(error);
			showAlert({
				message: "Banned Patient Message was not updated",
				alertType: AlertType.error
			});
			return;
		} finally {
			setIsLoading(false);
		}
	}, [account, bannedPatientMessage, instance, showAlert]);

	const goback = useCallback(() => {
		navigate(ROUTES.centreOperations, {
			state: { tabOption: 3, clinic }
		});
	}, [clinic, navigate]);

	return (
		<>
			<div className="flex flex-col p-4 gap-4 ch-lightestgrey-background">
				<div
					className="flex"
					style={{ cursor: "pointer" }}
					onClick={() => goback()}
				>
					<img
						src="images/svg/arrow-left-black.svg"
						className="mr-4"
						alt="back to banned patients"
					/>
					<div className="ch-l-text">Banned Patients</div>
				</div>

				<div className="w-full mt-4">
					<div className="mb-4">
						{clinic?.name} Banned Patient Check-In Message
					</div>
					{isLoading && (
						<div className="flex justify-center">
							<CircularProgress size="80px" />
						</div>
					)}
					<textarea
						className="ch-border w-full"
						style={{
							resize: "none",
							padding: "4px 12px",
							backgroundColor: "unset"
						}}
						rows={4}
						ref={messageEl}
						defaultValue={bannedPatientMessage?.message}
					/>
				</div>
				<div className="flex gap-4">
					<button
						className="ch-button ch-button-primary justify-center"
						onClick={() => goback()}
					>
						Go Back
					</button>
					<button
						className="ch-button ch-button-secondary justify-center"
						onClick={() => updateBannedPatientMessage()}
					>
						Confirm
					</button>
				</div>
			</div>
		</>
	);
};
